import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import PublicShell from '../components/PublicShell';
import { Home, Shield, ShieldCheck, FileText, BookOpen, Database, LogOut, LogIn, DollarSign, Check, ArrowRight, Tag, Loader2, Sparkles, Send, Globe, Ticket } from 'lucide-react';
import LeadCaptureCTA from '../components/LeadCaptureCTA';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { path: '/', icon: Home, label: 'Home', color: '#0ea5e9' },
  { path: '/platform', icon: Shield, label: 'Intelligence Platform', auth: true, color: '#16a34a' },
  { path: '/documentation', icon: FileText, label: 'Documentation', color: '#2563eb' },
  { path: '/use-cases', icon: BookOpen, label: 'Use Cases', color: '#9333ea' },
  { path: '/sources', icon: Database, label: 'Research Database', color: '#0891b2' },
  { path: '/provenance', icon: ShieldCheck, label: 'Data Provenance', color: '#059669' },
  { path: '/pricing', icon: DollarSign, label: 'Pricing', color: '#ca8a04' },
];

// Base costs per tier (mirrored from admin)
// Default base costs — aligned with admin REPORT_COMPONENTS (chunked,
// 30 illustrations, audio for Sovereign, no Cinematic Brief). These are used only if
// the backend /pricing-config does not yet have published base_costs.
const DEFAULT_BASE_COSTS = { Scout: 1.04, Strategist: 3.58, Sovereign: 4.69 };

const TIER_FEATURES = {
  Scout: [
    '12 Scout-tier AI-generated sections',
    'AI Executive Brief in PDF',
    'Sources & references list (30-40 entries)',
    'Investment-grade PDF export (25-40 pages)',
    'Real-time World Bank economic data',
    'Country risk assessment & scoring',
  ],
  Strategist: [
    'Everything in Scout',
    '+ 10 Strategist-tier sections (22 total, chunked Part A+B)',
    'AI-generated section illustrations',
    'Investment infographic (one-page visual)',
    'Gamma auto-generated presentation deck',
    'Sector-specific competitive analysis',
    'Financial projections with sensitivity analysis',
  ],
  Sovereign: [
    'Everything in Strategist',
    '+ 9 Sovereign-tier sections (31 total, chunked Part A+B)',
    'IC-ready Investment Memo + 100-Day Playbook',
    'Professional audio narration (2-4 min)',
    'Priority AI generation queue',
    'Multi-country comparison analytics',
    'Dedicated account support',
  ],
};

const TIER_META = [
  { name: 'Scout',      color: '#004ac6', tagline: 'Reconnaissance intelligence',     cta: 'Get Started' },
  { name: 'Strategist', color: '#00A651', tagline: 'Strategic execution',             cta: 'Most Popular', popular: true },
  { name: 'Sovereign',  color: '#7c3aed', tagline: 'Deal-ready intelligence',          cta: 'Contact Sales' },
];

export default function PublicPricingPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  useSEO({
    title: 'Pricing — Sovereign IQ | Country Intelligence Reports from $499',
    description: 'Transparent pricing for Sovereign IQ country blueprints. Scout, Strategist and Sovereign tiers with audit-ready citations, PDF export and live data. Volume discounts for DFIs and IPAs.',
    keywords: 'country intelligence pricing, market entry report cost, emerging markets research subscription, DFI research package, IPA investment promotion pricing, sovereign risk report cost',
    canonical: 'https://sovereigniq.org/pricing',
  });

  const go = (path, auth) => navigate(auth && !user ? '/login' : path);

  const [markup, setMarkup] = useState(1000);
  const [promo, setPromo] = useState(null);
  const [config, setConfig] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);
  const [luckyCountry, setLuckyCountry] = useState('');
  const [luckySector, setLuckySector] = useState('');
  const [luckyMessage, setLuckyMessage] = useState('');
  const [luckySubmitting, setLuckySubmitting] = useState(false);
  const [luckySubmitted, setLuckySubmitted] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [couponRedeeming, setCouponRedeeming] = useState(false);
  const [couponRedeemed, setCouponRedeemed] = useState(false);
  const [couponResult, setCouponResult] = useState('');

  useEffect(() => {
    axios.get(`${API}/pricing-config`).then(res => {
      setMarkup(res.data.markup || 1000);
      const active = (res.data.promotions || []).find(p => p.active);
      setPromo(active || null);
      setConfig(res.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
    axios.get(`${API}/sectors`).then(res => setSectors(res.data.sectors || [])).catch(() => {});
    axios.get(`${API}/countries-list`).then(res => setCountries(res.data.countries || [])).catch(() => {});
  }, []);

  const handleLuckySubmit = async () => {
    if (!user) { navigate('/login'); return; }
    if (!luckyCountry.trim() || !luckySector.trim()) { toast.error('Please select a country and sector'); return; }
    setLuckySubmitting(true);
    try {
      await axios.post(`${API}/free-access-requests`, { country: luckyCountry, sector: luckySector, message: luckyMessage });
      toast.success('Request submitted! The admin will review it shortly.');
      setLuckySubmitted(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setLuckySubmitting(false); }
  };

  const handleRedeemCoupon = async () => {
    if (!user) { navigate('/login'); return; }
    if (!couponCode.trim()) { toast.error('Enter a coupon code'); return; }
    setCouponRedeeming(true);
    try {
      const res = await axios.post(`${API}/coupons/redeem`, { code: couponCode });
      setCouponRedeemed(true);
      setCouponResult(res.data.message);
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Invalid coupon'); }
    finally { setCouponRedeeming(false); }
  };

  const handleCheckout = async (packageId) => {
    if (!user) { navigate('/login'); return; }
    setCheckingOut(packageId);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId.toLowerCase(),
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setCheckingOut(null);
    }
  };

  const tiers = TIER_META.map(t => {
    // Prefer live base_costs from backend (set by admin on "Publish to Customers"),
    // fall back to DEFAULT_BASE_COSTS if the config has not been re-published yet.
    const liveBase = config.base_costs || {};
    const base = liveBase[t.name] != null ? liveBase[t.name] : DEFAULT_BASE_COSTS[t.name];
    const fullPrice = Math.round(base * markup);
    const discountedPrice = promo ? Math.round(fullPrice * (1 - promo.discount / 100)) : null;
    return { ...t, fullPrice, discountedPrice, features: TIER_FEATURES[t.name] };
  });

  return (
    <PublicShell user={user} onLogout={onLogout}>
      <div className="overflow-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
        <style>{`
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
          .animate-fadeInUp { animation: fadeInUp 0.5s ease-out forwards; }
          @keyframes slowSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .animate-slowSpin { animation: slowSpin 30s linear infinite; }
          .grid-bg { background-image: linear-gradient(rgba(0,74,198,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,74,198,0.05) 1px, transparent 1px); background-size: 40px 40px; }
          .tier-lift { transition: all 0.35s cubic-bezier(0.4,0,0.2,1); }
          .tier-lift:hover { transform: translateY(-6px); }
        `}</style>

        <div data-testid="public-pricing-page">
          {/* Promotion Banner */}
          {promo && config.show_discount_bar !== false && (
            <div className="max-w-5xl mx-auto mt-6 mb-6 bg-gradient-to-r from-[#004ac6] to-[#7c3aed] rounded-xl px-6 py-4 flex items-center justify-between animate-fadeInUp" data-testid="promo-banner">
              <div className="flex items-center gap-3">
                <Tag className="w-5 h-5 text-white" />
                <div>
                  <p className="text-white font-bold text-sm">{promo.name}</p>
                  <p className="text-white/60 text-[10px]">Limited time offer on all plans</p>
                </div>
              </div>
              <span className="bg-white text-[#004ac6] font-extrabold text-lg px-4 py-1.5 rounded-lg">{promo.discount}% OFF</span>
            </div>
          )}

          {/* Hero */}
          <section className="relative overflow-hidden bg-gradient-to-br from-[#f8f9ff] via-white to-[#eff4ff] border-b border-[#c3c6d7]/20">
            <div className="absolute inset-0 grid-bg opacity-60" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-gradient-to-br from-[#004ac6]/5 via-[#7c3aed]/5 to-transparent animate-slowSpin pointer-events-none" />
            <div className="relative text-center max-w-3xl mx-auto py-14 lg:py-20 px-6">
              {user?.role === 'Admin' && (
                <button onClick={() => navigate('/platform/pricing')}
                  data-testid="admin-edit-pricing-btn"
                  className="absolute top-6 right-6 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#004ac6] text-white hover:bg-[#003ea8] transition-all shadow-md">
                  <Sparkles className="w-3.5 h-3.5" /> Admin: Edit Pricing
                </button>
              )}
              <div className="inline-flex items-center gap-2 bg-white border border-[#c3c6d7]/40 rounded-full px-3 py-1 mb-5 shadow-sm animate-fadeInUp">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#434655]">Pricing · Pay per country-sector report</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-[#0b1c30] mb-4 leading-[1.05] animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
                Investment-grade intelligence,
                <br />
                <span className="bg-gradient-to-r from-[#004ac6] to-[#7c3aed] bg-clip-text text-transparent">priced for impact.</span>
              </h1>
              <p className="text-base text-[#434655] leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                Every package delivers a full country-sector blueprint — up to <strong>31 AI-generated investment-grade sections</strong> across 115 sectors — with a single purchase. Regenerate each section up to 10 times within 3 months.
              </p>
            </div>
          </section>

          <div className="p-6 lg:p-8">

          {/* Tier Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
            {tiers.map(tier => (
              <div key={tier.name}
                data-testid={`public-tier-${tier.name.toLowerCase()}`}
                className={`bg-white rounded-2xl overflow-hidden tier-lift ${tier.popular ? 'ring-2 shadow-xl' : 'shadow-sm hover:shadow-lg'}`}
                style={{ ringColor: tier.popular ? tier.color : undefined, borderTop: `4px solid ${tier.color}` }}>
                {tier.popular && (
                  <div className="text-center py-1.5 text-[10px] font-bold uppercase tracking-widest text-white" style={{ backgroundColor: tier.color }}>
                    Most Popular
                  </div>
                )}
                <div className="p-7">
                  <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: tier.color }}>{tier.name}</p>
                  <p className="text-[11px] text-[#737686] mb-5">{tier.tagline}</p>

                  {tier.discountedPrice ? (
                    <div className="mb-1">
                      <span className="text-lg text-[#c3c6d7] line-through font-semibold mr-2">${tier.fullPrice.toLocaleString()}</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-extrabold tracking-tighter text-[#0b1c30]">${tier.discountedPrice.toLocaleString()}</span>
                        <span className="text-sm text-[#737686] font-medium">/ report</span>
                      </div>
                      <span className="inline-block mt-1 text-[10px] font-bold text-white bg-red-500 px-2 py-0.5 rounded-full">SAVE ${(tier.fullPrice - tier.discountedPrice).toLocaleString()}</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-4xl font-extrabold tracking-tighter text-[#0b1c30]">${loaded ? tier.fullPrice.toLocaleString() : '...'}</span>
                      <span className="text-sm text-[#737686] font-medium">/ report</span>
                    </div>
                  )}
                  <p className="text-[10px] text-[#c3c6d7] mb-6">Per country blueprint &middot; Includes 10 regenerations per section within 3 months</p>

                  <button onClick={() => handleCheckout(tier.name)}
                    disabled={checkingOut === tier.name}
                    data-testid={`cta-${tier.name.toLowerCase()}`}
                    className="w-full py-2.5 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: tier.popular ? tier.color : 'transparent',
                      color: tier.popular ? 'white' : tier.color,
                      border: tier.popular ? 'none' : `2px solid ${tier.color}`,
                      opacity: checkingOut ? 0.7 : 1,
                    }}>
                    {checkingOut === tier.name ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> :
                      <>{user ? 'Subscribe Now' : tier.cta} <ArrowRight className="w-4 h-4" /></>}
                  </button>

                  <div className="h-px bg-[#c3c6d7]/30 my-6" />

                  <ul className="space-y-3">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-xs text-[#434655]">
                        <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: tier.color }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Coupon Code Redeem */}
          {config.show_coupon_bar !== false && (
          <div className="max-w-5xl mx-auto mb-8" data-testid="coupon-redeem-bar">
            <div className="bg-gradient-to-r from-[#f3f0ff] to-[#ece8ff] border border-[#7c3aed]/20 rounded-xl overflow-hidden">
              <div className="px-6 py-3 flex items-center gap-2 bg-[#7c3aed]/10">
                <Ticket className="w-4 h-4 text-[#7c3aed]" />
                <span className="text-sm font-bold text-[#7c3aed]">Have a Coupon?</span>
                <span className="text-xs text-[#7c3aed]/60 ml-1">— Redeem your code for a free report</span>
              </div>
              {couponRedeemed ? (
                <div className="px-6 py-4 text-center">
                  <p className="text-sm font-semibold text-green-700">{couponResult}</p>
                  <p className="text-xs text-[#434655] mt-1">Head to your dashboard to create your report.</p>
                </div>
              ) : (
                <div className="px-6 py-4">
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <input type="text" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter coupon code"
                        className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-4 py-2.5 text-sm text-[#0b1c30] font-mono tracking-widest focus:ring-1 focus:ring-[#7c3aed] outline-none placeholder:font-sans placeholder:tracking-normal"
                        data-testid="coupon-input"
                        onKeyDown={e => e.key === 'Enter' && handleRedeemCoupon()} />
                    </div>
                    <button onClick={handleRedeemCoupon} disabled={couponRedeeming}
                      data-testid="coupon-redeem-btn"
                      className="bg-[#7c3aed] text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-[#6d28d9] transition-all flex items-center gap-1.5 shrink-0 disabled:opacity-50">
                      {couponRedeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}
                      {user ? 'Redeem' : 'Sign In to Redeem'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Be Lucky Bar */}
          {config.show_lucky_bar !== false && (
          <div className="max-w-5xl mx-auto mb-8" data-testid="be-lucky-bar">
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-xl overflow-hidden">
              <div className="px-6 py-3 flex items-center gap-2 bg-amber-400/20">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-bold text-amber-800">Be Lucky</span>
                <span className="text-xs text-amber-600 ml-1">— Ask the administrator for complimentary reports</span>
              </div>
              {luckySubmitted ? (
                <div className="px-6 py-4 text-center">
                  <p className="text-sm font-semibold text-green-700">Request submitted successfully!</p>
                  <p className="text-xs text-[#434655] mt-1">A confirmation email has been sent to you.</p>
                </div>
              ) : (
                <div className="px-6 py-4">
                  <div className="flex gap-3 items-end flex-wrap">
                    <div className="flex-1 min-w-[160px]">
                      <label className="text-[10px] text-[#434655] block mb-1">Country</label>
                      <select value={luckyCountry} onChange={e => setLuckyCountry(e.target.value)}
                        className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-amber-400 outline-none appearance-none"
                        data-testid="lucky-country">
                        <option value="">Select a country...</option>
                        {countries.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <label className="text-[10px] text-[#434655] block mb-1">Sector</label>
                      <select value={luckySector} onChange={e => setLuckySector(e.target.value)}
                        className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-amber-400 outline-none appearance-none"
                        data-testid="lucky-sector">
                        <option value="">Select a sector...</option>
                        {sectors.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[140px]">
                      <label className="text-[10px] text-[#434655] block mb-1">Message (optional)</label>
                      <input type="text" value={luckyMessage} onChange={e => setLuckyMessage(e.target.value)}
                        placeholder="Why you'd like a free report..."
                        className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-amber-400 outline-none"
                        data-testid="lucky-message" />
                    </div>
                    <button onClick={handleLuckySubmit} disabled={luckySubmitting}
                      data-testid="lucky-submit"
                      className="bg-amber-500 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-amber-600 transition-all flex items-center gap-1.5 shrink-0 disabled:opacity-50">
                      {luckySubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {user ? 'Submit Request' : 'Sign In to Submit'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* What's Included */}
          <div className="max-w-5xl mx-auto mb-16">
            <h2 className="text-lg font-extrabold tracking-tight text-[#0b1c30] text-center mb-6">What Every Report Includes</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { num: '12', label: 'AI-Generated Sections', sub: 'Pyramid Principle' },
                { num: '195', label: 'Countries Available', sub: 'All sovereign nations' },
                { num: '115', label: 'Industry Sectors', sub: 'Including Climate Tech' },
                { num: '1,000+', label: 'Research Sources', sub: '192 API integrations' },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl p-5 shadow-sm text-center">
                  <p className="text-2xl font-extrabold tracking-tighter text-[#004ac6]">{s.num}</p>
                  <p className="text-xs font-semibold text-[#0b1c30] mt-1">{s.label}</p>
                  <p className="text-[10px] text-[#737686] mt-0.5">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-[#0b1c30] via-[#1a365d] to-[#004ac6] rounded-2xl p-10 mb-8">
            <h2 className="text-xl font-extrabold tracking-tight text-white mb-2">Ready to Generate Your First Blueprint?</h2>
            <p className="text-sm text-white/60 mb-6">Register for an account and start analyzing any country in minutes.</p>
            <button onClick={() => navigate('/login')} data-testid="cta-register"
              className="bg-white text-[#004ac6] font-bold text-sm px-8 py-3 rounded-lg hover:bg-white/90 transition-all">
              Create Account
            </button>
          </div>

          <LeadCaptureCTA source_page="pricing" headline="Enterprise or multi-country plan?" subline="For volume licenses, regional portfolios, or bespoke DFI arrangements, let's structure a plan that fits." interests={[ 'Private equity / investment fund', 'Development finance (DFI, IFC, AfDB)', 'Multinational corporate strategy', 'Consulting firm / advisory', 'Government / sovereign client', 'Enterprise volume license', 'Other' ]} />
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
